<template>
  <div id="pageReporte4">
    <v-container fluid>
      <v-row>
        <v-col>
          <v-card class="elevation-2">
            <v-toolbar color="primary" dark flat class="report-toolbar">
              <v-icon class="mr-2">mdi-warehouse</v-icon>
              <v-toolbar-title>Stock inmovilizado</v-toolbar-title>
              <v-spacer></v-spacer>
              <report-filters :fields="['location_id', 'condition', 'inactivity_threshold']" @change="onFilterChange"></report-filters>
              <v-btn outlined :loading="loadingExport" @click="downloadExcel">
                <v-icon left small>mdi-microsoft-excel</v-icon>
                <span class="d-none d-sm-inline">Descargar Excel</span>
              </v-btn>
              <v-btn outlined class="ml-2" :loading="loadingPdf" @click="downloadPdf(4)">
                <v-icon left small>mdi-file-pdf-box</v-icon>
                <span class="d-none d-sm-inline">Descargar PDF</span>
              </v-btn>
            </v-toolbar>

            <v-card-text class="pt-6">
              <div class="text-body-2 grey--text text--darken-1 mb-6">
                Vehículos que no se están moviendo: cuánto tiempo llevan en cada Estado Proceso y en qué ubicación. Cada día
                de inmovilización es un coste oculto para la concesión.
              </div>

              <v-progress-linear v-if="loadingPreview" indeterminate color="primary" class="mb-6"></v-progress-linear>

              <div v-else>
                <v-alert v-if="!previewItems.length" type="info" text dense>
                  No hay datos para mostrar
                </v-alert>

                <template v-else>
                  <!-- KPIs -->
                  <div class="kpi-grid mb-8">
                    <div class="kpi-card">
                      <v-icon size="30" color="blue darken-2">mdi-car-clock</v-icon>
                      <div class="kpi-number">{{ kpis.conEstado }}</div>
                      <div class="kpi-label">Vehículos con Estado Proceso</div>
                    </div>
                    <div class="kpi-card">
                      <v-icon size="30" color="red darken-1">mdi-alert-circle-outline</v-icon>
                      <div class="kpi-number">{{ kpis.masDe15 }}</div>
                      <div class="kpi-label">Más de 15 días</div>
                    </div>
                    <div class="kpi-card">
                      <v-icon size="30" color="orange darken-1">mdi-sleep</v-icon>
                      <div class="kpi-number">{{ kpis.sinActividad }}</div>
                      <div class="kpi-label">Recepcionados sin actividad</div>
                      <div class="kpi-subtitle">más de {{ inactivityThreshold }} días</div>
                    </div>
                    <div class="kpi-card">
                      <v-icon size="30" color="purple darken-1">mdi-timer-outline</v-icon>
                      <div class="kpi-number">{{ kpis.mediaDias }} d</div>
                      <div class="kpi-label">Media en Estado Proceso</div>
                    </div>
                  </div>

                  <!-- Antigüedad por Estado Proceso -->
                  <div class="report-section-title">
                    <v-icon small color="primary" class="mr-1">mdi-timer-sand</v-icon>
                    Antigüedad por Estado Proceso
                  </div>
                  <v-simple-table class="report-table mb-8" dense>
                    <thead>
                      <tr>
                        <th>Estado Proceso</th>
                        <th v-for="tramo in tramos" :key="tramo.key" class="text-center">{{ tramo.label }}</th>
                        <th class="text-center">Total</th>
                        <th class="text-center">Media días</th>
                        <th class="text-center">Máx. días</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in byEstado" :key="row.estado">
                        <td>{{ row.estado }}</td>
                        <td v-for="(tramo, index) in tramos" :key="tramo.key" class="text-center">
                          <span v-if="row.tramos[tramo.key] > 0" class="bucket-pill" :class="`bucket-${index}`">{{ row.tramos[tramo.key] }}</span>
                          <span v-else class="grey--text">0</span>
                        </td>
                        <td class="text-center font-weight-medium">{{ row.total }}</td>
                        <td class="text-center">{{ row.mediaDias }}</td>
                        <td class="text-center">
                          <span class="bucket-pill" :class="daysBucketClass(row.maxDias)">{{ row.maxDias }}</span>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>

                  <!-- Por ubicación -->
                  <div class="report-section-title">
                    <v-icon small color="primary" class="mr-1">mdi-map-marker-outline</v-icon>
                    Por ubicación
                  </div>
                  <v-simple-table class="report-table mb-8" dense>
                    <thead>
                      <tr>
                        <th>Ubicación</th>
                        <th v-for="estado in estadoColumns" :key="estado" class="text-center">{{ estado }}</th>
                        <th class="text-center">Total</th>
                        <th class="text-center">&gt;15 días</th>
                        <th class="text-center">Recepc. sin actividad &gt;{{ inactivityThreshold }} d</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in byLocation" :key="row.location">
                        <td class="font-weight-bold">{{ row.location }}</td>
                        <td v-for="estado in estadoColumns" :key="estado" class="text-center">{{ row.estados[estado] || 0 }}</td>
                        <td class="text-center font-weight-bold">{{ row.total }}</td>
                        <td class="text-center">
                          <span v-if="row.masDe15 > 0" class="bucket-pill bucket-3">{{ row.masDe15 }}</span>
                          <span v-else>0</span>
                        </td>
                        <td class="text-center">
                          <!-- Pill roja cuando todos los vehículos de la ubicación están sin actividad -->
                          <span v-if="row.total > 0 && row.sinActividad === row.total" class="bucket-pill bucket-3">{{ row.sinActividad }}</span>
                          <span v-else>{{ row.sinActividad }}</span>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>

                  <!-- Detalle -->
                  <div class="report-section-title">
                    <v-icon small color="primary" class="mr-1">mdi-format-list-bulleted</v-icon>
                    Detalle (más de 15 días)
                  </div>
                  <v-alert v-if="!detailRows.length" type="success" text dense>
                    Ningún vehículo supera los 15 días en su Estado Proceso
                  </v-alert>
                  <v-data-table
                    v-else
                    :headers="detailHeaders"
                    :items="detailRows"
                    class="report-table mb-4"
                    dense
                    item-key="id"
                    sort-by="days_in_estado"
                    sort-desc
                    :footer-props="{ itemsPerPageOptions: [10, 25, 50] }"
                    :items-per-page="10"
                  >
                    <template v-slot:item.vehicle="{ item }">
                      <div class="font-weight-medium">{{ item.vehicle }}</div>
                      <div class="text-caption grey--text">{{ vehicleSubtitle(item) }}</div>
                      <div class="text-caption grey--text">Bastidor: {{ item.frame || '—' }}</div>
                    </template>
                    <template v-slot:item.location="{ item }">
                      {{ item.location || '—' }}
                    </template>
                    <template v-slot:item.estado_proceso_start="{ item }">
                      {{ formatDateOnly(item.estado_proceso_start) }}
                    </template>
                    <template v-slot:item.days_in_estado="{ item }">
                      <span class="bucket-pill" :class="daysBucketClass(item.days_in_estado)">{{ item.days_in_estado }}</span>
                    </template>
                    <template v-slot:item.open_todos="{ item }">
                      {{ item.open_todos > 0 ? item.open_todos : '—' }}
                    </template>
                  </v-data-table>
                </template>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import moment from 'moment'
import ReportService from '../../services/ReportService'
import errorHandler from '../../mixins/errorHandler'
import reportPdf from '../../mixins/reportPdf'
import ReportFilters from '../../components/ReportFilters'

const SIN_ESTADO = 'Sin estado'
const SIN_UBICACION = 'Sin ubicación'
// Valor por defecto del backend (inactivity_threshold); solo se usa para el texto del KPI
const INACTIVITY_THRESHOLD = 30

// Los guiones de los tramos son en-dash, tal y como los devuelve el backend
const TRAMOS = [
  { key: '0–2', label: '0–2 días' },
  { key: '3–7', label: '3–7 días' },
  { key: '8–15', label: '8–15 días' },
  { key: 'Más de 15', label: '>15 días' }
]

const average = (values) => values.length ? values.reduce((a, b) => a + b, 0) / values.length : null
const formatDecimal = (value) => value !== null ? value.toFixed(1).replace('.', ',') : '—'

export default {
  name: 'Reporte4',

  components: { ReportFilters },

  mixins: [errorHandler, reportPdf],

  data: () => ({
    params: {},
    previewItems: [],
    loadingPreview: true,
    loadingExport: false,
    inactivityThreshold: INACTIVITY_THRESHOLD,
    tramos: TRAMOS,

    detailHeaders: [
      { text: 'Vehículo', value: 'vehicle' },
      { text: 'Condición', value: 'condition' },
      { text: 'Ubicación', value: 'location' },
      { text: 'Estado Proceso', value: 'estado_proceso' },
      { text: 'Desde', value: 'estado_proceso_start' },
      { text: 'Días', value: 'days_in_estado' },
      { text: 'TO-DOs abiertos', value: 'open_todos' }
    ]
  }),

  computed: {
    conEstadoRows () {
      return this.previewItems.filter(row => row.estado_proceso !== SIN_ESTADO && row.days_in_estado != null)
    },

    kpis () {
      const conEstado = this.previewItems.filter(row => row.estado_proceso !== SIN_ESTADO)

      return {
        conEstado: conEstado.length,
        masDe15: this.previewItems.filter(row => row.days_in_estado > 15).length,
        sinActividad: this.previewItems.filter(row => row.inactive).length,
        mediaDias: formatDecimal(average(this.conEstadoRows.map(row => row.days_in_estado)))
      }
    },

    byEstado () {
      const groups = {}
      this.conEstadoRows.forEach(row => {
        if (!groups[row.estado_proceso]) groups[row.estado_proceso] = []
        groups[row.estado_proceso].push(row)
      })

      return Object.keys(groups).map(estado => {
        const rows = groups[estado]
        const days = rows.map(row => row.days_in_estado)
        const tramos = {}
        TRAMOS.forEach(tramo => { tramos[tramo.key] = rows.filter(row => row.tramo === tramo.key).length })

        return {
          estado,
          tramos,
          total: TRAMOS.reduce((sum, tramo) => sum + tramos[tramo.key], 0),
          mediaDias: formatDecimal(average(days)),
          maxDias: Math.max(...days)
        }
      }).sort((a, b) => b.total - a.total)
    },

    // Estados Proceso presentes en los datos (dinámicos), "Sin estado" siempre al final
    estadoColumns () {
      const counts = {}
      this.previewItems.forEach(row => { counts[row.estado_proceso] = (counts[row.estado_proceso] || 0) + 1 })

      return Object.keys(counts)
        .filter(estado => estado !== SIN_ESTADO)
        .sort((a, b) => counts[b] - counts[a])
        .concat(counts[SIN_ESTADO] ? [SIN_ESTADO] : [])
    },

    byLocation () {
      const groups = {}
      this.previewItems.forEach(row => {
        const location = row.location || SIN_UBICACION
        if (!groups[location]) groups[location] = []
        groups[location].push(row)
      })

      return Object.keys(groups).map(location => {
        const rows = groups[location]
        const estados = {}
        rows.forEach(row => { estados[row.estado_proceso] = (estados[row.estado_proceso] || 0) + 1 })

        return {
          location,
          estados,
          total: rows.length,
          masDe15: rows.filter(row => row.days_in_estado > 15).length,
          sinActividad: rows.filter(row => row.inactive).length
        }
      }).sort((a, b) => b.total - a.total)
    },

    detailRows () {
      return this.previewItems.filter(row => row.days_in_estado > 15)
    }
  },

  methods: {
    onFilterChange (params) {
      this.params = params
      this.loadPreview()
    },

    daysBucketClass (days) {
      if (days === null || days === undefined) return ''
      if (days <= 2) return 'bucket-0'
      if (days <= 7) return 'bucket-1'
      if (days <= 15) return 'bucket-2'
      return 'bucket-3'
    },

    vehicleSubtitle (item) {
      const parts = [`Matrícula: ${item.plate || '—'}`]
      if (item.iic) parts.push(`IIC ${item.iic}`)
      return parts.join(' · ')
    },

    formatDateOnly (value) {
      if (!value) return '—'
      return moment.unix(value).format('DD/MM/YYYY')
    },

    async loadPreview () {
      try {
        this.loadingPreview = true
        const { data } = await ReportService.getReport4(this.params)
        this.previewItems = data.data || []
      } catch (error) {
        this.handleApiError(error, 'Error al cargar los datos del reporte')
      } finally {
        this.loadingPreview = false
      }
    },

    async downloadExcel () {
      try {
        this.loadingExport = true
        const response = await ReportService.downloadReport4(this.params)
        const blob = response.data

        if (!blob || blob.size === 0) {
          throw new Error('El archivo descargado está vacío')
        }

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
        link.download = `reporte4_${timestamp}.xlsx`
        link.style.display = 'none'

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        this.$toast.success('Reporte descargado correctamente')
      } catch (error) {
        this.handleApiError(error, 'Error al descargar el reporte')
      } finally {
        this.loadingExport = false
      }
    }
  },

  created () {
    this.loadPreview()
  }
}
</script>

<style scoped>
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
}

.kpi-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px 12px;
  text-align: center;
}

.kpi-number {
  font-size: 30px;
  font-weight: 600;
  line-height: 1.3;
  margin-top: 4px;
}

.kpi-label {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.7);
}

.kpi-subtitle {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
}

.report-section-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 12px;
}

.report-table {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.report-table >>> thead th {
  background-color: #fafafa;
  font-weight: 600 !important;
}

.bucket-pill {
  display: inline-block;
  min-width: 28px;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 500;
  text-align: center;
}

.bucket-0 {
  background-color: #E8F5E9;
  color: #2E7D32;
}

.bucket-1 {
  background-color: #FFF8E1;
  color: #F9A825;
}

.bucket-2 {
  background-color: #FFF3E0;
  color: #EF6C00;
}

.bucket-3 {
  background-color: #FFEBEE;
  color: #C62828;
}
</style>
